/**
 * 中国象棋游戏逻辑
 * 9×10棋盘，包含将、士、象、马、车、炮、兵七种棋子
 */

import { Player, XiangqiPlayer, Position, GameMode, Difficulty, XiangqiPiece, XiangqiPieceType, XiangqiMove, XiangqiGameState, XIANGQI_PIECE_VALUES, XIANGQI_PIECE_NAMES } from '../types/game';

// ==================== 基础工具函数 ====================

/**
 * 检查位置是否在棋盘内
 */
export const isValidPosition = (position: Position): boolean => {
  return position.row >= 0 && position.row < 10 && position.col >= 0 && position.col < 9;
};

/**
 * 检查位置是否在九宫格内
 */
export const isInPalace = (position: Position, player: XiangqiPlayer): boolean => {
  if (player === 'red') {
    return position.row >= 7 && position.row <= 9 && position.col >= 3 && position.col <= 5;
  } else if (player === 'black') {
    return position.row >= 0 && position.row <= 2 && position.col >= 3 && position.col <= 5;
  } else {
    return false;
  }
};

/**
 * 检查位置是否在己方领域
 */
export const isInOwnHalf = (position: Position, player: XiangqiPlayer): boolean => {
  if (player === 'red') {
    return position.row >= 5; // 红方在下半部
  } else if (player === 'black') {
    return position.row <= 4; // 黑方在上半部
  } else {
    return false;
  }
};

/**
 * 检查是否有棋子阻挡（用于象和马）
 */
export const isBlocked = (board: (XiangqiPiece | null)[][], from: Position, to: Position): boolean => {
  const rowDiff = to.row - from.row;
  const colDiff = to.col - from.col;

  // 象的移动：走田字，检查田字中心是否有棋子
  if (Math.abs(rowDiff) === 2 && Math.abs(colDiff) === 2) {
    const centerRow = from.row + rowDiff / 2;
    const centerCol = from.col + colDiff / 2;
    return board[centerRow][centerCol] !== null;
  }

  // 马的移动：走日字，检查蹩马腿
  if ((Math.abs(rowDiff) === 2 && Math.abs(colDiff) === 1) ||
      (Math.abs(rowDiff) === 1 && Math.abs(colDiff) === 2)) {
    let blockRow, blockCol;

    if (Math.abs(rowDiff) === 2) {
      blockRow = from.row + rowDiff / 2;
      blockCol = from.col;
    } else {
      blockRow = from.row;
      blockCol = from.col + colDiff / 2;
    }

    return board[blockRow][blockCol] !== null;
  }

  return false;
};

// ==================== 棋盘和棋子设置 ====================

/**
 * 创建初始中国象棋棋盘 (9×10)
 */
export const createXiangqiBoard = (): (XiangqiPiece | null)[][] => {
  const board: (XiangqiPiece | null)[][] = Array(10).fill(null).map(() => Array(9).fill(null));

  // 放置黑方棋子（上方）
  // 第一行
  board[0][0] = { type: 'chariot', player: 'black' };
  board[0][1] = { type: 'horse', player: 'black' };
  board[0][2] = { type: 'elephant', player: 'black' };
  board[0][3] = { type: 'advisor', player: 'black' };
  board[0][4] = { type: 'general', player: 'black' };
  board[0][5] = { type: 'advisor', player: 'black' };
  board[0][6] = { type: 'elephant', player: 'black' };
  board[0][7] = { type: 'horse', player: 'black' };
  board[0][8] = { type: 'chariot', player: 'black' };

  // 第三行 - 炮
  board[2][1] = { type: 'cannon', player: 'black' };
  board[2][7] = { type: 'cannon', player: 'black' };

  // 第四行 - 兵
  for (let col = 0; col < 9; col += 2) {
    board[3][col] = { type: 'soldier', player: 'black' };
  }

  // 放置红方棋子（下方）
  // 第九行
  board[9][0] = { type: 'chariot', player: 'red' };
  board[9][1] = { type: 'horse', player: 'red' };
  board[9][2] = { type: 'elephant', player: 'red' };
  board[9][3] = { type: 'advisor', player: 'red' };
  board[9][4] = { type: 'general', player: 'red' };
  board[9][5] = { type: 'advisor', player: 'red' };
  board[9][6] = { type: 'elephant', player: 'red' };
  board[9][7] = { type: 'horse', player: 'red' };
  board[9][8] = { type: 'chariot', player: 'red' };

  // 第七行 - 炮
  board[7][1] = { type: 'cannon', player: 'red' };
  board[7][7] = { type: 'cannon', player: 'red' };

  // 第六行 - 兵
  for (let col = 0; col < 9; col += 2) {
    board[6][col] = { type: 'soldier', player: 'red' };
  }

  return board;
};

/**
 * 创建初始游戏状态
 */
export const createInitialGameState = (
  gameMode: GameMode = 'pvp',
  difficulty?: Difficulty
): XiangqiGameState => {
  const board = createXiangqiBoard();

  // 找到将/帅的位置
  let redGeneralPos: Position | undefined;
  let blackGeneralPos: Position | undefined;

  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 9; col++) {
      const piece = board[row][col];
      if (piece && piece.type === 'general') {
        if (piece.player === 'red') {
          redGeneralPos = { row, col };
        } else {
          blackGeneralPos = { row, col };
        }
      }
    }
  }

  return {
    board,
    currentPlayer: 'red', // 红方先行
    status: 'playing',
    winner: null,
    moves: [],
    gameHistory: [],
    gameMode,
    gameType: 'xiangqi',
    difficulty,
    isAIThinking: false,

    // 中国象棋特有状态
    selectedPiece: undefined,
    possibleMoves: [],
    isCheck: false,
    isCheckmate: false,
    isStalemate: false,
    capturedPieces: { black: [], red: [] },
    moveCount: 0,
    repeatedMoves: 0,
    fiftyMoveRule: 0,

    // 棋盘特有信息
    redGeneralPos,
    blackGeneralPos,

    // 游戏统计
    totalMoves: 0,
    captureMoves: 0,
    checkMoves: 0,
  };
};

// ==================== 游戏规则验证 ====================

/**
 * 检查将/帅的移动是否合法
 */
export const canGeneralMove = (
  from: Position,
  to: Position,
  player: XiangqiPlayer,
  board: (XiangqiPiece | null)[][]
): boolean => {
  // 只能在九宫格内移动
  if (!isInPalace(to, player)) {
    return false;
  }

  // 只能移动一格，且只能是上下左右
  const rowDiff = Math.abs(to.row - from.row);
  const colDiff = Math.abs(to.col - from.col);

  // 将/帅只能走一步，不能走日字或斜线
  const isOneStepMove = (rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1);

  if (!isOneStepMove) {
    return false;
  }

  // 检查移动后是否会造成将帅对面（将帅不能直接对面）
  // 创建测试棋盘，模拟移动后的情况
  const testBoard = board.map(row => [...row]);
  const piece = testBoard[from.row][from.col];

  if (piece) {
    testBoard[to.row][to.col] = piece;
    testBoard[from.row][from.col] = null;

    // 检查移动后是否与敌方将帅对面
    let enemyGeneralPos: Position | null = null;
    for (let row = 0; row < 10; row++) {
      for (let col = 0; col < 9; col++) {
        const testPiece = testBoard[row][col];
        if (testPiece && testPiece.type === 'general' && testPiece.player !== player) {
          enemyGeneralPos = { row, col };
          break;
        }
      }
      if (enemyGeneralPos) break;
    }

    // 如果找到敌方将帅且在同一列，检查中间是否有棋子
    if (enemyGeneralPos && to.col === enemyGeneralPos.col) {
      let hasPieceBetween = false;
      const minRow = Math.min(to.row, enemyGeneralPos.row);
      const maxRow = Math.max(to.row, enemyGeneralPos.row);

      for (let row = minRow + 1; row < maxRow; row++) {
        if (testBoard[row][to.col] !== null) {
          hasPieceBetween = true;
          break;
        }
      }

      // 如果中间没有棋子，则造成将帅对面，这是非法的
      if (!hasPieceBetween) {
        return false;
      }
    }
  }

  return true;
};

/**
 * 检查士的移动是否合法
 */
export const canAdvisorMove = (from: Position, to: Position, player: XiangqiPlayer): boolean => {
  // 只能在九宫格内移动
  if (!isInPalace(to, player)) {
    return false;
  }

  // 斜着走一格
  const rowDiff = Math.abs(to.row - from.row);
  const colDiff = Math.abs(to.col - from.col);

  return rowDiff === 1 && colDiff === 1;
};

/**
 * 检查象/相的移动是否合法
 */
export const canElephantMove = (
  from: Position,
  to: Position,
  player: XiangqiPlayer,
  board: (XiangqiPiece | null)[][]
): boolean => {
  // 不能过河
  if (!isInOwnHalf(to, player)) {
    return false;
  }

  // 走田字
  const rowDiff = Math.abs(to.row - from.row);
  const colDiff = Math.abs(to.col - from.col);

  if (rowDiff === 2 && colDiff === 2) {
    // 检查田字中心是否有棋子（塞象眼）
    const centerRow = from.row + (to.row - from.row) / 2;
    const centerCol = from.col + (to.col - from.col) / 2;
    return board[centerRow][centerCol] === null;
  }

  return false;
};

/**
 * 检查马的移动是否合法
 */
export const canHorseMove = (
  from: Position,
  to: Position,
  board: (XiangqiPiece | null)[][]
): boolean => {
  // 走日字
  const rowDiff = Math.abs(to.row - from.row);
  const colDiff = Math.abs(to.col - from.col);

  if ((rowDiff === 2 && colDiff === 1) || (rowDiff === 1 && colDiff === 2)) {
    // 检查蹩马腿
    let blockRow, blockCol;

    if (rowDiff === 2) {
      blockRow = from.row + (to.row - from.row) / 2;
      blockCol = from.col;
    } else {
      blockRow = from.row;
      blockCol = from.col + (to.col - from.col) / 2;
    }

    return board[blockRow][blockCol] === null;
  }

  return false;
};

/**
 * 检查车的移动是否合法
 */
export const canChariotMove = (
  from: Position,
  to: Position,
  board: (XiangqiPiece | null)[][]
): boolean => {
  // 只能走直线
  if (from.row !== to.row && from.col !== to.col) {
    return false;
  }

  // 检查路径上是否有棋子阻挡
  const rowStep = to.row === from.row ? 0 : (to.row - from.row) / Math.abs(to.row - from.row);
  const colStep = to.col === from.col ? 0 : (to.col - from.col) / Math.abs(to.col - from.col);

  let currentRow = from.row + rowStep;
  let currentCol = from.col + colStep;

  while (currentRow !== to.row || currentCol !== to.col) {
    if (board[currentRow][currentCol] !== null) {
      return false;
    }
    currentRow += rowStep;
    currentCol += colStep;
  }

  return true;
};

/**
 * 检查炮的移动是否合法
 */
export const canCannonMove = (
  from: Position,
  to: Position,
  board: (XiangqiPiece | null)[][]
): boolean => {
  // 只能走直线
  if (from.row !== to.row && from.col !== to.col) {
    return false;
  }

  // 计算路径上的棋子数量
  const rowStep = to.row === from.row ? 0 : (to.row - from.row) / Math.abs(to.row - from.row);
  const colStep = to.col === from.col ? 0 : (to.col - from.col) / Math.abs(to.col - from.col);

  let currentRow = from.row + rowStep;
  let currentCol = from.col + colStep;
  let pieceCount = 0;

  while (currentRow !== to.row || currentCol !== to.col) {
    if (board[currentRow][currentCol] !== null) {
      pieceCount++;
    }
    currentRow += rowStep;
    currentCol += colStep;
  }

  const targetPiece = board[to.row][to.col];

  // 如果目标位置为空，路径上不能有棋子
  if (targetPiece === null) {
    return pieceCount === 0;
  }

  // 如果目标位置有敌方棋子，路径上必须有且只有一个棋子（炮架）
  return pieceCount === 1;
};

/**
 * 检查兵/卒的移动是否合法
 */
export const canSoldierMove = (from: Position, to: Position, player: XiangqiPlayer): boolean => {
  const rowDiff = to.row - from.row;
  const colDiff = Math.abs(to.col - from.col);

  if (player === 'red') {
    // 红方兵向上走
    if (from.row >= 5) {
      // 未过河，只能向前
      return rowDiff === -1 && colDiff === 0;
    } else {
      // 过河后，可以向前或左右
      return (rowDiff === -1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1);
    }
  } else if (player === 'black') {
    // 黑方卒向下走
    if (from.row <= 4) {
      // 未过河，只能向前
      return rowDiff === 1 && colDiff === 0;
    } else {
      // 过河后，可以向前或左右
      return (rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1);
    }
  } else {
    return false;
  }
};

/**
 * 检查棋子移动是否合法
 */
export const canPieceMove = (
  from: Position,
  to: Position,
  piece: XiangqiPiece,
  board: (XiangqiPiece | null)[][]
): boolean => {
  // 不能吃自己的棋子
  const targetPiece = board[to.row][to.col];
  if (targetPiece && targetPiece.player === piece.player) {
    return false;
  }

  switch (piece.type) {
    case 'general':
      return canGeneralMove(from, to, piece.player, board);
    case 'advisor':
      return canAdvisorMove(from, to, piece.player);
    case 'elephant':
      return canElephantMove(from, to, piece.player, board);
    case 'horse':
      return canHorseMove(from, to, board);
    case 'chariot':
      return canChariotMove(from, to, board);
    case 'cannon':
      return canCannonMove(from, to, board);
    case 'soldier':
      return canSoldierMove(from, to, piece.player);
    default:
      return false;
  }
};

// ==================== 游戏操作 ====================

/**
 * 获取棋子的所有可能移动
 */
export const getPieceMoves = (
  gameState: XiangqiGameState,
  position: Position
): XiangqiMove[] => {
  const piece = gameState.board[position.row][position.col];
  if (!piece) return [];

  const moves: XiangqiMove[] = [];

  // 检查所有可能的目标位置
  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 9; col++) {
      const to = { row, col };
      if (canPieceMove(position, to, piece, gameState.board)) {
        const targetPiece = gameState.board[row][col];
        const move: XiangqiMove = {
          player: piece.player,
          from: position,
          to,
          captures: targetPiece || undefined,
        };

        // 检查是否是将军移动
        const testBoard = makeTestMove(gameState.board, move);
        if (isKingInCheck(piece.player, testBoard)) {
          move.isCheck = true;
        }

        moves.push(move);
      }
    }
  }

  return moves;
};

/**
 * 获取玩家的所有可能移动
 */
export const getAllPossibleMoves = (
  gameState: XiangqiGameState,
  player: XiangqiPlayer
): XiangqiMove[] => {
  const allMoves: XiangqiMove[] = [];

  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 9; col++) {
      const piece = gameState.board[row][col];
      if (piece && piece.player === player) {
        const position = { row, col };
        const moves = getPieceMoves(gameState, position);
        allMoves.push(...moves);
      }
    }
  }

  // 过滤掉会导致自己被将军的移动
  return allMoves.filter(move => {
    const testBoard = makeTestMove(gameState.board, move);
    return !isKingInCheck(player, testBoard);
  });
};

/**
 * 检查将/帅是否被将军
 */
export const isKingInCheck = (
  player: XiangqiPlayer,
  board: (XiangqiPiece | null)[][]
): boolean => {
  // 找到将/帅的位置
  let kingPos: Position | null = null;

  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 9; col++) {
      const piece = board[row][col];
      if (piece && piece.type === 'general' && piece.player === player) {
        kingPos = { row, col };
        break;
      }
    }
    if (kingPos) break;
  }

  if (!kingPos) return false;

  // 检查是否有敌方棋子可以攻击将/帅
  const enemy = player === 'red' ? 'black' : 'red';

  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 9; col++) {
      const piece = board[row][col];
      if (piece && piece.player === enemy) {
        const from = { row, col };
        if (canPieceMove(from, kingPos, piece, board)) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * 创建测试棋盘（用于验证移动合法性）
 */
export const makeTestMove = (
  board: (XiangqiPiece | null)[][],
  move: XiangqiMove
): (XiangqiPiece | null)[][] => {
  const newBoard = board.map(row => [...row]);
  const piece = newBoard[move.from.row][move.from.col];

  if (piece) {
    newBoard[move.to.row][move.to.col] = piece;
    newBoard[move.from.row][move.from.col] = null;
  }

  return newBoard;
};

/**
 * 执行移动
 */
export const executeMove = (
  gameState: XiangqiGameState,
  move: XiangqiMove
): XiangqiGameState => {
  const newBoard = gameState.board.map(row => [...row]);
  const piece = newBoard[move.from.row][move.from.col];

  if (!piece) return gameState;

  // 移动棋子
  newBoard[move.to.row][move.to.col] = piece;
  newBoard[move.from.row][move.from.col] = null;

  // 处理吃子
  const newCapturedPieces = { ...gameState.capturedPieces };
  if (move.captures) {
    const capturedPlayer = move.captures.player; // Use the actual piece that was captured
    newCapturedPieces[capturedPlayer] = [...newCapturedPieces[capturedPlayer], move.captures];
  }

  // 更新将/帅位置
  let newRedGeneralPos = gameState.redGeneralPos;
  let newBlackGeneralPos = gameState.blackGeneralPos;

  if (piece.type === 'general') {
    if (piece.player === 'red') {
      newRedGeneralPos = move.to;
    } else {
      newBlackGeneralPos = move.to;
    }
  }

  // 创建新游戏状态
  const newGameState: XiangqiGameState = {
    ...gameState,
    board: newBoard,
    currentPlayer: gameState.currentPlayer === 'red' ? 'black' : 'red',
    moves: [...gameState.moves, move],
    gameHistory: [...gameState.gameHistory, gameState],
    capturedPieces: newCapturedPieces,
    redGeneralPos: newRedGeneralPos,
    blackGeneralPos: newBlackGeneralPos,

    // 更新统计
    totalMoves: gameState.totalMoves + 1,
    captureMoves: gameState.captureMoves + (move.captures ? 1 : 0),
    checkMoves: gameState.checkMoves + (move.isCheck ? 1 : 0),

    // 重置选择状态
    selectedPiece: undefined,
    possibleMoves: [],
  };

  // 检查将军状态
  newGameState.isCheck = isKingInCheck(newGameState.currentPlayer, newBoard);

  return newGameState;
};

/**
 * 检查游戏是否结束
 */
export const checkGameEnd = (
  gameState: XiangqiGameState
): { isEnd: boolean; winner: XiangqiPlayer | null; reason: string } => {
  const currentPlayer = gameState.currentPlayer;
  const possibleMoves = getAllPossibleMoves(gameState, currentPlayer);

  // 没有合法移动
  if (possibleMoves.length === 0) {
    if (isKingInCheck(currentPlayer, gameState.board)) {
      // 被将死
      const winner = currentPlayer === 'red' ? 'black' : 'red';
      return { isEnd: true, winner, reason: `${currentPlayer === 'red' ? '红方' : '黑方'}被将死！` };
    } else {
      // 和棋
      return { isEnd: true, winner: null, reason: '和棋！' };
    }
  }

  // 检查将帅是否还在（理论上不会发生）
  let redKingExists = false;
  let blackKingExists = false;

  for (let row = 0; row < 10; row++) {
    for (let col = 0; col < 9; col++) {
      const piece = gameState.board[row][col];
      if (piece && piece.type === 'general') {
        if (piece.player === 'red') redKingExists = true;
        if (piece.player === 'black') blackKingExists = true;
      }
    }
  }

  if (!redKingExists) {
    return { isEnd: true, winner: 'black', reason: '红方帅被吃！' };
  }
  if (!blackKingExists) {
    return { isEnd: true, winner: 'red', reason: '黑方将被吃！' };
  }

  // TODO: 可以添加其他和棋规则，如50回合规则、重复移动等

  return { isEnd: false, winner: null, reason: '' };
};

// ==================== 游戏状态管理 ====================

/**
 * 选择棋子
 */
export const selectPiece = (
  gameState: XiangqiGameState,
  position: Position
): XiangqiGameState => {
  const piece = gameState.board[position.row][position.col];

  if (!piece || piece.player !== gameState.currentPlayer) {
    return {
      ...gameState,
      selectedPiece: undefined,
      possibleMoves: []
    };
  }

  // 获取可能的移动
  const possibleMoves = getPieceMoves(gameState, position);

  return {
    ...gameState,
    selectedPiece: position,
    possibleMoves
  };
};

/**
 * 取消选择棋子
 */
export const deselectPiece = (gameState: XiangqiGameState): XiangqiGameState => {
  return {
    ...gameState,
    selectedPiece: undefined,
    possibleMoves: []
  };
};

/**
 * 悔棋功能
 */
export const undoMove = (gameState: XiangqiGameState): XiangqiGameState => {
  if (gameState.gameHistory.length === 0) {
    return gameState; // 没有历史记录，无法悔棋
  }

  // 获取上一个游戏状态
  const previousState = gameState.gameHistory[gameState.gameHistory.length - 1];

  return {
    ...previousState,
    selectedPiece: undefined,
    possibleMoves: [],
  };
};

/**
 * 执行移动
 */
export const makeMove = (
  gameState: XiangqiGameState,
  move: XiangqiMove
): XiangqiGameState => {
  // 执行移动
  const newGameState = executeMove(gameState, move);

  // 检查游戏是否结束
  const gameEnd = checkGameEnd(newGameState);
  if (gameEnd.isEnd) {
    return {
      ...newGameState,
      status: 'finished',
      winner: gameEnd.winner,
      isCheckmate: gameEnd.winner !== null
    };
  }

  return newGameState;
};

export default {
  // 基础函数
  isValidPosition,
  isInPalace,
  isInOwnHalf,
  isBlocked,

  // 棋盘和棋子
  createXiangqiBoard,
  createInitialGameState,

  // 游戏规则
  canPieceMove,
  getPieceMoves,
  getAllPossibleMoves,
  isKingInCheck,
  checkGameEnd,

  // 游戏操作
  executeMove,
  selectPiece,
  deselectPiece,
  makeMove,
  undoMove
};